// api/axiosInstance.ts
import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-hot-toast';
import { notification } from 'antd';
import { emitUpgradeRequired } from '../../contexts/UpgradeModalContext';
import { clearAuthStorage } from '../../utils/authStorage';

declare global {
  interface Window {
    __sessionExpiredShown?: boolean;
    __lastCorrelationId?: string;
  }
}

// ── Correlation ID ────────────────────────────────────────────────────────────
// One id per outbound request, sent as X-Correlation-ID and propagated by every
// backend, so a single user action can be traced across all services in Seq.
// Backends echo it back; we stash the latest for support/debugging.
const genCorrelationId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const captureCorrelationId = (response: AxiosResponse): AxiosResponse => {
  const cid = (response.headers?.['x-correlation-id'] ??
    response.headers?.['X-Correlation-ID']) as string | undefined;
  if (cid) window.__lastCorrelationId = cid;
  return response;
};

// ── Debounced server-error notification ──────────────────────────────────────
// Multiple concurrent API calls can all fail at once (e.g. dashboard loading
// 5 endpoints when the server is down). We show exactly one notification per
// 4-second window instead of spamming the user.
let serverErrorCooldown = false;

const showServerError = (title: string, description?: string) => {
  if (serverErrorCooldown) return;
  serverErrorCooldown = true;

  notification.error({
    message: title,
    description: description || undefined,
    duration: 6,
    placement: 'topRight',
    style: { maxWidth: 440 },
  });

  setTimeout(() => { serverErrorCooldown = false; }, 4000);
};

// ── API response message extraction ──────────────────────────────────────────
// Handles all known shapes from the various backends in this project:
//   .NET API:   { message, title }
//   Payroll:    { error, details, errorCode, correlationId }
//   NestJS:     { message, statusCode }
//   Plain text: "Some error string"
const extractApiParts = (data: any): { primary: string | null; secondary: string | null } => {
  if (!data) return { primary: null, secondary: null };

  if (typeof data === 'string' && data.length < 400) {
    return { primary: data, secondary: null };
  }

  const primary =
    (typeof data.message === 'string' ? data.message : null) ||
    (typeof data.title === 'string' ? data.title : null) ||
    (typeof data.error === 'string' ? data.error : null) ||
    null;

  const secondary =
    (typeof data.details === 'string' ? data.details : null) ||
    (typeof data.description === 'string' ? data.description : null) ||
    null;

  // Array messages (NestJS validation errors)
  if (!primary && Array.isArray(data.message)) {
    return { primary: data.message.join(' · '), secondary: null };
  }

  return { primary, secondary };
};

const getErrorParts = (
  error: AxiosError
): { title: string; description?: string } => {
  const status = error.response?.status;
  const data = error.response?.data as any;
  const { primary, secondary } = extractApiParts(data);

  // Network / no response
  if (!status) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return { title: 'Request timed out', description: 'Please check your connection and try again.' };
    }
    return { title: 'Cannot reach server', description: 'Check your internet connection or try again shortly.' };
  }

  const descriptions: Record<number, string> = {
    400: primary || 'Invalid request — please check your input and try again.',
    401: 'Your session has expired. Please log in again.',
    403: "You don't have permission to perform this action.",
    404: primary || 'The requested resource was not found.',
    408: 'Request timed out. Please try again.',
    409: primary || 'A conflict occurred. The data may have been modified by someone else.',
    422: primary || 'The submitted data is invalid. Please review and try again.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: primary || 'Something went wrong on the server. Please try again later.',
    502: primary || 'Server gateway error. Please try again in a moment.',
    503: primary || 'Service temporarily unavailable. Please try again later.',
    504: primary || 'Server took too long to respond. Please try again.',
  };

  const fallback = status >= 500
    ? (primary || 'A server error occurred. Please try again later.')
    : (primary || 'An unexpected error occurred. Please try again.');

  const desc = descriptions[status] ?? fallback;

  // For 5xx: show the details line as a second line if present
  if (status >= 500 && secondary && secondary !== desc) {
    return { title: desc, description: secondary };
  }

  // For 400/422 with extra details
  if ((status === 400 || status === 422) && secondary && secondary !== desc) {
    return { title: desc, description: secondary };
  }

  return { title: desc };
};

// HRMS-11: overwrite AxiosError.message with the backend's user-facing text so that
// EVERY consumer — the shared parseApiError() helper AND the many call sites across
// modules that read raw `error.message` / `toast.error(error.message)` — surfaces the
// meaningful backend message instead of the generic "Request failed with status code N".
// Priority mirrors the ticket: details → message → error. No-op when the body carries
// none of those, leaving the original axios message as the last-resort fallback.
const normalizeErrorMessage = (error: AxiosError): void => {
  const data = error.response?.data as
    | { details?: unknown; message?: unknown; error?: unknown }
    | undefined;
  if (!data || typeof data !== 'object') return;
  const backendMsg = data.details ?? data.message ?? data.error;
  if (typeof backendMsg === 'string' && backendMsg.trim()) {
    error.message = backendMsg;
  }
};

// ── Axios instance factory ────────────────────────────────────────────────────
const createAxiosInstance: any = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout: 600000, // 10 min — covers large file uploads
    maxContentLength: 50 * 1024 * 1024,
    maxBodyLength: 50 * 1024 * 1024,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      const token = localStorage.getItem('token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (config.headers) {
        config.headers['X-Correlation-ID'] = genCorrelationId();
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => captureCorrelationId(response),
    (error: AxiosError) => {
      normalizeErrorMessage(error);
      const status = error.response?.status;
      const requestUrl = error.config?.url || '';

      const isLoginRequest =
        requestUrl.includes('Login/login') || requestUrl.includes('/login') || requestUrl.includes('/auth/login');
      const isProfileUpdate =
        requestUrl.includes('update-profile') || requestUrl.includes('update-password');

      // 401 — session expired (but don't redirect on login or profile update attempts)
      if (status === 401) {
        if (isLoginRequest || isProfileUpdate) {
          return Promise.reject(error);
        }

        // Client-portal pages manage their own auth flow — don't auto-logout
        const isClientPortalPage = window.location.pathname.startsWith('/client/');
        if (isClientPortalPage) {
          return Promise.reject(error);
        }

        if (!window.__sessionExpiredShown) {
          window.__sessionExpiredShown = true;
          toast.error('Session expired. Please login again.');
          // clearAuthStorage();
          // window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // 402 — subscription / plan restriction
      if (status === 402) {
        const body = error.response?.data as any ?? {};
        emitUpgradeRequired({
          errorCode: body.errorCode ?? body.code ?? 'PlanUpgradeRequired',
          message: body.message ?? body.title ?? undefined,
          currentPlan: body.currentPlan ?? undefined,
          requiredPlan: body.requiredPlan ?? undefined,
          feature: body.feature ?? undefined,
          description: body.description ?? body.details ?? undefined,
        });
        return Promise.reject(error);
      }

      // 403 — permission denied
      if (status === 403) {
        toast.error("You don't have permission to perform this action.");
        return Promise.reject(error);
      }

      // 5xx server errors — rich notification with title + details
      if (status && status >= 500) {
        const { title, description } = getErrorParts(error);
        showServerError(title, description);
        return Promise.reject(error);
      }

      // Network errors (no response — server down, DNS failure, etc.)
      if (!error.response && !status) {
        const { title, description } = getErrorParts(error);
        showServerError(title, description);
        return Promise.reject(error);
      }

      // 4xx client errors — pass through; callers handle these individually.
      return Promise.reject(error);
    }
  );

  return instance;
};

const coreApi = createAxiosInstance(import.meta.env.VITE_BASE_URL);
export const authApi = createAxiosInstance(import.meta.env.VITE_AUTH_BASE_URL);
export const employeeApi = createAxiosInstance(import.meta.env.VITE_EMPLOYEE_BASE_URL || import.meta.env.VITE_AUTH_BASE_URL);
export const payrollApi = createAxiosInstance(import.meta.env.VITE_PAYROLL_BASE_URL);
export const userApi = createAxiosInstance(import.meta.env.VITE_USER_BASE_URL);
export const recruiterApi = createAxiosInstance(import.meta.env.VITE_RECRUITER_BASE_URL);
export const Api = createAxiosInstance(import.meta.env.VITE_BASIC_BASE_URL);

// ── NestJS API ────────────────────────────────────────────────────────────────
const nestBaseUrl = import.meta.env.VITE_BASIC_BASE_URL || 'http://localhost:9010/api/v1';
const nestInstance = axios.create({
  baseURL: nestBaseUrl,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});
nestInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.headers) {
      config.headers['X-Correlation-ID'] = genCorrelationId();
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);
nestInstance.interceptors.response.use(
  (response: AxiosResponse) => captureCorrelationId(response),
  (error: AxiosError) => {
    normalizeErrorMessage(error);
    const status = error.response?.status;
    if (status === 402) {
      const body = error.response?.data as any ?? {};
      emitUpgradeRequired({
        errorCode: body.errorCode ?? body.code ?? 'PlanUpgradeRequired',
        message: body.message ?? body.title ?? undefined,
        currentPlan: body.currentPlan ?? undefined,
        requiredPlan: body.requiredPlan ?? undefined,
        feature: body.feature ?? undefined,
        description: body.description ?? body.details ?? undefined,
      });
    }
    if ((status && status >= 500) || (!error.response && !status)) {
      const { title, description } = getErrorParts(error);
      showServerError(title, description);
    }
    return Promise.reject(error);
  },
);
export const NestApi = nestInstance;

export default coreApi;
